import { useJobContext } from 'pages/Job/context';
import { useMemo } from 'react';
import { calculateJobTotal, getRateCost } from 'utils';
import { getJobDepositPayment } from 'utils/Job-functions';

export type JobFinanceOptionsDisplayType = 'all' | 'selected';

export interface JobFinanceFinanceSubsidyHook {
  loan: number;
  deposit?: number;
  depositSubsidy?: number;
  subsidy?: number;
  payout?: number;
  isCashDeposit: boolean;
}

export default function useJobFinanceFinanceSubsidy(): JobFinanceFinanceSubsidyHook {
  const { job, isLoading } = useJobContext();

  const total = useMemo(
    () => (job?.job_items ? calculateJobTotal(job.job_items).totalIncVat : 0),
    [job],
  );

  const depositPayment = useMemo(() => getJobDepositPayment(job), [job]);

  const isCashDeposit = useMemo(
    () => depositPayment?.payment_option.payment_method === 'cash',
    [depositPayment],
  );

  const deposit = useMemo(
    () =>
      job?.customer_options?.deposit_value?.amount ||
      job?.deposit_value?.amount ||
      0,
    [job],
  );

  const subsidy = useMemo(() => {
    if (isLoading) return undefined;
    const rate = job?.customer_options?.finance_option;
    if (!rate) return undefined;
    return getRateCost(rate, total, deposit);
  }, [isLoading, job, total, deposit]);

  const depositSubsidy = useMemo(() => {
    if (isLoading) return undefined;
    if (isCashDeposit) return undefined;
    return deposit * 0.01;
  }, [isLoading, isCashDeposit, deposit]);

  const loan = useMemo(() => total - deposit, [total, deposit]);

  const payout = useMemo(() => {
    if (!subsidy) return undefined;
    const totalPayout = isCashDeposit ? loan : total;
    const totalSubsidy = subsidy + (depositSubsidy ?? 0);
    return totalPayout - totalSubsidy;
  }, [isCashDeposit, total, loan, subsidy, depositSubsidy]);

  return {
    loan,
    deposit,
    subsidy,
    depositSubsidy,
    payout,
    isCashDeposit,
  };
}
